import { CheckCheck, EllipsisVertical, Flag, Move, Pen, RefreshCw, Trash } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Invoice, InvoiceLineItem } from 'src/redux/billing/billing.types';
import { fieldValue, formatCurrency } from '../../utils/billing-utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import BorderedSelect from 'src/components/form-new/bordered-select';
import BorderedInput from 'src/components/form-new/bordered-input';
import { formatNumber, formatPercentage } from './utils';
import { useDispatch } from 'react-redux';
import InvoiceLineItemNoteModal from './invoice-line-item-note-modal';
import { Company } from '../../types/billing.types';

interface InvoiceDrawerLineTableProps {
  invoice: Invoice | null | undefined;
  skus?: Array<{ id: string; name: string }>;
  invoices: Invoice[];
  companies: Company[];
  updateLineItem: (lineItemId: string, field: string, value: any) => void;
}

const InvoiceDrawerLineTable: React.FC<InvoiceDrawerLineTableProps> = ({
  invoice,
  skus = [],
  invoices,
  companies,
  updateLineItem,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState<InvoiceLineItem | null>(null);
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const dispatch = useDispatch();

  const handleUpdateLineItem = async(lineItemId: string, field: string, value: any) => {
    setIsLoading(true);

    await updateLineItem(lineItemId, field, value);
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedLineItem && invoice) {
      const updatedLineItem = invoice?.invoice_line_items.find((lineItem) => lineItem.id === selectedLineItem?.id);
      if (updatedLineItem) {
        setSelectedLineItem(updatedLineItem);
      }
    }
  }, [selectedLineItem, invoice]);

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute top-2 left-2 flex justify-left items-center z-50 p-3 bg-blue-500/80 text-white rounded-full w-fit">
          <RefreshCw className="h-5 w-5 text-white animate-bounce animate-spin" />
        </div>
      )}
      <Table className={`w-full ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>

        <TableHeader>
          <TableRow>
            <TableHead className='text-gray-600 text-sm font-bold'>
              #
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[200px] min-w-[200px] max-w-[200px]'>
              SKU/Product
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold'>
              Description
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[125px] min-w-[125px] max-w-[125px]'>
              Quantity
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[125px] min-w-[125px] max-w-[125px]'>
              Rate
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[100px] min-w-[100px] max-w-[100px]'>
              Margin %
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[150px] min-w-[150px] max-w-[150px]'>
              Amount
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[20px] min-w-[20px] max-w-[20px]' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoice?.invoice_line_items.filter((lineItem) => !lineItem.hide).map((lineItem) => (
            <>
              {lineItem.notes && (
                <TableRow
                  key={lineItem.id}
                  className="mb-2"
                >
                  <TableCell
                    colSpan={8}
                    className="bg-gray-100 p-2 rounded-md"
                  >
                    <p className="text-gray-500 text-sm italic">
                      {lineItem.notes}
                    </p>
                  </TableCell>
                </TableRow>
              )}
              <TableRow
                key={lineItem.id}
                className="border-b border-gray-200"
              >
                <TableCell className='text-gray-600 text-sm py-7'>
                  {lineItem.ext_line_item_id}
                </TableCell>
                <TableCell className='text-gray-600 text-sm'>
                  <BorderedSelect
                    options={skus?.map((sku) => ({ value: sku.id, label: sku.name }))}
                    value={fieldValue(lineItem, 'sku')}
                    onChange={(value) => {handleUpdateLineItem(lineItem.id, 'sku_override', value.target.value); console.log('value', value);}}
                  />
                </TableCell>
                <TableCell className='text-gray-600 text-sm'>
                  <BorderedInput
                    label={ lineItem.platform && lineItem.channel ? lineItem.platform.toLowerCase().replace('_', ' ') + '  (' + lineItem.channel.toLowerCase().replace('_', ' ') + ')' : ''}
                    value={lineItem.name || ''}
                    onChange={(value) => handleUpdateLineItem(lineItem.id, 'name', value.target.value)}
                  />
                </TableCell>
                <TableCell className='text-gray-600 text-sm'>
                  <BorderedInput
                    value={fieldValue(lineItem, 'qty')}
                    formatDisplay={(value) => formatNumber(Number(value))}
                    onBlur={(value) => handleUpdateLineItem(lineItem.id, 'qty_override', value.target.value)}
                  />
                  {/* {formatNumber(lineItem.qty)} */}
                </TableCell>
                <TableCell className='text-gray-600 text-sm cursor-not-allowed'>
                  {formatCurrency(Number(fieldValue(lineItem, 'rate')), 'USD', 2, 6)}
                  {/* {lineItem.amount_to_bill && formatCurrency(lineItem.amount_to_bill / lineItem.qty || 0)} */}
                </TableCell>
                <TableCell className='text-gray-600 text-sm'>
                  <BorderedInput
                    label={formatCurrency(Number(lineItem.margin), 'USD', 0, 0)}
                    value={fieldValue(lineItem, 'margin_pct')}
                    formatDisplay={(value) => formatPercentage(Number(value))}
                    onBlur={(value) => handleUpdateLineItem(lineItem.id, 'margin_pct_override', value.target.value)}
                    onKeyDown={(value: React.KeyboardEvent<HTMLInputElement>) => {
                      if (value.key === 'Enter') {
                        handleUpdateLineItem(lineItem.id, 'margin_pct_override', value.currentTarget.value);
                      }
                    }}
                  />
                </TableCell>
                <TableCell className='text-gray-600 text-sm'>
                  <BorderedInput
                    value={fieldValue(lineItem, 'advertiser_spend')}
                    formatDisplay={(value) => formatCurrency(Number(value))}
                    onBlur={(value) => handleUpdateLineItem(lineItem.id, 'advertiser_spend_override', value.target.value)}
                    onKeyDown={(value: React.KeyboardEvent<HTMLInputElement>) => {
                      if (value.key === 'Enter') {
                        handleUpdateLineItem(lineItem.id, 'advertiser_spend_override', value.currentTarget.value);
                      }
                    }}
                  />
                  {/* {formatCurrency(lineItem.amount_to_bill || 0)} */}
                </TableCell>

                <TableCell className='text-gray-600 text-sm'>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size='sm'
                        variant='ghost'
                        className='text-gray-600 text-sm'
                      >
                        <EllipsisVertical className='h-4 w-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={() => handleUpdateLineItem(lineItem.id, 'invoice_line_item_status', 'approved')}
                      >
                        <CheckCheck className='h-4 w-4' />
                        Approve
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedLineItem(lineItem);
                          setIsNoteModalOpen(true);
                        }}
                      >
                        <Pen className='h-4 w-4' />
                        Note
                      </DropdownMenuItem>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <DropdownMenuItem>
                            <Move className='h-4 w-4' />
                            Move
                          </DropdownMenuItem>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align='start'
                          side='bottom'
                          className='w-fit !max-h-[300px] !overflow-y-auto z-50'
                        >
                          {/* <div>
                            <input
                              type="text"
                              placeholder="Search companies"
                              className="w-full text-sm h-8 border-b border-gray-400 text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                              value={moveSearchQuery}
                              onChange={(e) => setMoveSearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div> */}
                          {invoices.map((invoice) => (
                            <DropdownMenuItem
                              key={invoice.id}
                              onClick={() => updateLineItem(lineItem.id, 'invoice', invoice.id)}
                            >
                              <div className='flex flex-col text-sm font-bold text-gray-700'>
                                {companies.find((company) => company.id === invoice.company)?.company_name}
                                <span className='text-xs font-light text-gray-500'>
                                  {invoice.invoice_line_items.length}
                                  {' '}
                                  line item
                                  {invoice.invoice_line_items.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenuItem
                        onClick={() => handleUpdateLineItem(lineItem.id, 'attention_needed', !lineItem.attention_needed)}
                      >
                        <Flag className='h-4 w-4' />
                        Flag
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        //   turn into a hard delete
                        onClick={() => handleUpdateLineItem(lineItem.id, 'hide', !lineItem.hide)}
                      >
                        <Trash className='h-4 w-4' />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            </>
          ))}
        </TableBody>
      </Table>
      <InvoiceLineItemNoteModal
        invoiceLineItem={selectedLineItem}
        isOpen={isNoteModalOpen}
        onOpenChange={setIsNoteModalOpen}
        updateLineItem={handleUpdateLineItem}
      />
      {/* <InvoiceLineItemMoveModal
        invoiceLineItem={selectedLineItem}
        isOpen={isMoveModalOpen}
        onOpenChange={setIsMoveModalOpen}
        updateLineItem={handleUpdateLineItem}
      /> */}
    </div>
  );
};

export default InvoiceDrawerLineTable;
